/**
 * WorkflowSheets — Slide-up panels triggered by Iris suggestions or quick action chips.
 * Each workflow covers the bottom ~40% of the preview area.
 * The user's site stays visible above.
 *
 * Workflows: Photos, Colors, Fonts, SEO, Blog, AddPage
 */
import { useState, useRef, memo } from 'react';
import { X, Upload, Palette, Type, Search, FileText, Plus, Loader2, Check, RefreshCw, ChevronRight, Film, Image as ImageIcon, Link } from 'lucide-react';

// ═══ Sheet wrapper ═══
const Sheet = ({ title, icon: Icon, onClose, children }) => (
  <div data-testid="workflow-sheet" style={{
    position: 'absolute', bottom: 0, left: 0, right: 0, maxHeight: '45%', minHeight: '200px',
    background: '#fff', borderTop: '1px solid #e8e3dc', borderRadius: '16px 16px 0 0',
    boxShadow: '0 -4px 20px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column',
    animation: 'sheetSlideUp 0.3s cubic-bezier(.25,.46,.45,.94)',
    zIndex: 10,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f0ece6', flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {Icon && <Icon size={16} style={{ color: '#C9A84C' }} />}
        <span style={{ fontFamily: 'var(--sans)', fontSize: '15px', fontWeight: 600, color: '#2a2725' }}>{title}</span>
      </div>
      <button onClick={onClose} style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f2ee', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#8c8782', transition: 'background .15s' }}
        onMouseEnter={e => e.currentTarget.style.background = '#e8e3dc'}
        onMouseLeave={e => e.currentTarget.style.background = '#f5f2ee'}>
        <X size={14} />
      </button>
    </div>
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
      {children}
    </div>
    <style>{`@keyframes sheetSlideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
  </div>
);

// ═══ Photos Workflow ═══
export const PhotosSheet = memo(({ mediaItems, onSwap, onUpload, onClose }) => {
  const fileRef = useRef(null);
  const [activeItem, setActiveItem] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');

  return (
    <Sheet title="Your Photos" icon={ImageIcon} onClose={onClose}>
      <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782', marginBottom: '14px' }}>
        Tap any image to replace it with your own work.
      </p>
      <input type="file" ref={fileRef} accept="image/*" style={{ display: 'none' }} onChange={e => {
        const f = e.target.files?.[0];
        if (f && activeItem) { onUpload(f, activeItem.url); setActiveItem(null); }
        e.target.value = '';
      }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
        {mediaItems.map((item, i) => (
          <div key={i} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', aspectRatio: '4/3', background: '#f0ece6', cursor: 'pointer' }}
            onClick={() => {
              if (item.type === 'image') { setActiveItem(item); fileRef.current?.click(); }
              else { setActiveItem(activeItem?.index === item.index ? null : item); }
            }}>
            {item.type === 'image' ? (
              <img src={item.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(201,168,76,0.04)' }}>
                <Film size={20} style={{ color: '#C9A84C' }} />
              </div>
            )}
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity .15s' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0'}>
              <span style={{ padding: '4px 10px', background: '#fff', borderRadius: '6px', fontSize: '11px', fontFamily: 'var(--sans)', fontWeight: 500 }}>
                Replace
              </span>
            </div>
          </div>
        ))}
      </div>
      {/* Video URL input for active video item */}
      {activeItem?.type === 'video' && (
        <div style={{ marginTop: '12px', display: 'flex', gap: '6px' }}>
          <input type="text" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="Paste Vimeo or YouTube URL"
            style={{ flex: 1, padding: '10px 12px', fontSize: '13px', fontFamily: 'var(--sans)', background: '#faf8f5', border: '1px solid #e8e3dc', borderRadius: '8px', outline: 'none', color: '#2a2725' }} />
          <button onClick={() => { if (videoUrl.trim()) { onSwap(activeItem.url, videoUrl.trim()); setActiveItem(null); setVideoUrl(''); } }}
            disabled={!videoUrl.trim()} style={{ padding: '10px 14px', background: videoUrl.trim() ? '#C9A84C' : '#e8e3dc', color: videoUrl.trim() ? '#fff' : '#8c8782', border: 'none', borderRadius: '8px', cursor: videoUrl.trim() ? 'pointer' : 'default' }}>
            <Link size={14} />
          </button>
        </div>
      )}
    </Sheet>
  );
});

// ═══ Colors Workflow ═══
export const ColorsSheet = memo(({ colors, onSwap, onClose }) => (
  <Sheet title="Your Colors" icon={Palette} onClose={onClose}>
    <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782', marginBottom: '14px' }}>
      Click any color to change it across your entire site.
    </p>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
      {colors.map(c => (
        <label key={c} style={{ cursor: 'pointer', textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: c, border: '2px solid #e8e3dc', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', transition: 'transform .15s' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.12)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />
          <input type="color" defaultValue={c} onChange={e => onSwap(c, e.target.value)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
          <span style={{ display: 'block', fontSize: '9px', fontFamily: 'var(--sans)', color: '#8c8782', marginTop: '4px' }}>{c}</span>
        </label>
      ))}
    </div>
    {colors.length === 0 && <p style={{ fontSize: '13px', color: '#8c8782', fontStyle: 'italic' }}>No colors detected. Ask Iris to change colors instead.</p>}
  </Sheet>
));

// ═══ Fonts Workflow ═══
export const FontsSheet = memo(({ fonts, pairings, onSwap, onClose }) => (
  <Sheet title="Font Pairing" icon={Type} onClose={onClose}>
    {fonts.length > 0 && (
      <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: '#8c8782', marginBottom: '12px' }}>
        Current: <strong style={{ color: '#2a2725' }}>{fonts.join(' + ')}</strong>
      </p>
    )}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
      {(pairings || []).map(([h, b], i) => (
        <button key={i} onClick={() => { if (fonts[0]) onSwap(fonts[0], h); if (fonts[1]) onSwap(fonts[1], b); onClose(); }}
          style={{ padding: '14px', textAlign: 'left', border: '1px solid #e8e3dc', borderRadius: '10px', background: '#faf8f5', cursor: 'pointer', transition: 'all .15s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#C9A84C'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e3dc'; e.currentTarget.style.transform = 'none'; }}>
          <span style={{ fontFamily: h + ', serif', fontSize: '15px', color: '#2a2725', display: 'block', marginBottom: '2px' }}>{h}</span>
          <span style={{ fontSize: '11px', color: '#8c8782' }}>+ {b}</span>
        </button>
      ))}
    </div>
  </Sheet>
));

PhotosSheet.displayName = 'PhotosSheet';
ColorsSheet.displayName = 'ColorsSheet';
FontsSheet.displayName = 'FontsSheet';
